import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Switch,
    CircularProgress,
    Alert,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../../../../../config/axiosConfig';
import { API } from '../../../../../utils/apiRoutes';
import { toPersianDigits } from '../../../../../utils/convertNumbers';

function extractApiError(err, fallback) {
    const data = err?.response?.data;
    if (!data) {
        return err?.message || fallback;
    }
    if (typeof data.detail === 'string') {
        return data.detail;
    }
    if (Array.isArray(data.detail)) {
        return data.detail.join(' ');
    }
    if (typeof data.name === 'object' && data.name?.[0]) {
        return data.name[0];
    }
    if (typeof data === 'string') {
        return data;
    }
    return fallback;
}

export default function ExpenseCategoryManager({
    categories = [],
    loading = false,
    fetchError = '',
    onClose,
    onRefresh,
}) {
    const [items, setItems] = useState(categories);
    const [actionError, setActionError] = useState('');
    const [newName, setNewName] = useState('');
    const [saving, setSaving] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [confirmTarget, setConfirmTarget] = useState(null);

    useEffect(() => {
        setItems(categories);
    }, [categories]);

    const showInitialLoading = loading && items.length === 0;

    const handleAdd = async () => {
        const name = newName.trim();
        if (!name) return;
        setSaving(true);
        setActionError('');
        try {
            const response = await api.post(API.expenseCategories, { name });
            const created = response.data;
            if (created?.id) {
                setItems((prev) => [...prev, created]);
            }
            setNewName('');
            onRefresh?.();
        } catch (err) {
            setActionError(extractApiError(err, 'خطا در افزودن دسته‌بندی'));
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (category) => {
        if (!category?.id || busyId === category.id) return;

        const previousActive = category.is_active;
        const nextActive = !previousActive;

        setActionError('');
        setBusyId(category.id);
        setItems((prev) =>
            prev.map((item) =>
                item.id === category.id ? { ...item, is_active: nextActive } : item
            )
        );

        try {
            await api.patch(`${API.expenseCategories}${category.id}/`, {
                is_active: nextActive,
            });
            onRefresh?.();
        } catch (err) {
            setItems((prev) =>
                prev.map((item) =>
                    item.id === category.id ? { ...item, is_active: previousActive } : item
                )
            );
            setActionError(extractApiError(err, 'خطا در به‌روزرسانی دسته‌بندی'));
        } finally {
            setBusyId(null);
        }
    };

    const requestDelete = (category) => {
        if (!category.can_delete || !category?.id || busyId === category.id) return;
        setActionError('');
        setConfirmTarget(category);
    };

    const performDelete = async () => {
        const category = confirmTarget;
        setConfirmTarget(null);
        if (!category?.id) return;

        setBusyId(category.id);
        const snapshot = items;
        setItems((prev) => prev.filter((item) => item.id !== category.id));

        try {
            await api.delete(`${API.expenseCategories}${category.id}/`);
            onRefresh?.();
        } catch (err) {
            setItems(snapshot);
            setActionError(extractApiError(err, 'خطا در حذف دسته‌بندی'));
        } finally {
            setBusyId(null);
        }
    };

    const displayError = actionError || fetchError;

    return (
        <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                دسته‌بندی‌های غیرفعال در فرم ثبت هزینه نمایش داده نمی‌شوند، اما در گزارش‌های قبلی باقی می‌مانند.
            </Typography>

            {displayError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {displayError}
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    label="دسته‌بندی جدید"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAdd();
                        }
                    }}
                />
                <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                    onClick={handleAdd}
                    disabled={saving || !newName.trim()}
                >
                    افزودن
                </Button>
            </Box>

            {showInitialLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : items.length === 0 && !fetchError ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    دسته‌بندی‌ای یافت نشد.
                </Typography>
            ) : (
                <List dense sx={{ bgcolor: 'background.default', borderRadius: 1 }}>
                    {items.map((category) => (
                        <ListItem
                            key={category.id}
                            secondaryAction={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title={category.is_active ? 'غیرفعال کردن' : 'فعال کردن'}>
                                        <Switch
                                            edge="end"
                                            checked={Boolean(category.is_active)}
                                            disabled={busyId === category.id}
                                            onChange={() => handleToggleActive(category)}
                                            inputProps={{ 'aria-label': `toggle-${category.name}` }}
                                        />
                                    </Tooltip>
                                    <Tooltip title={
                                        category.can_delete
                                            ? 'حذف'
                                            : 'این دسته‌بندی در هزینه‌ها استفاده شده است'
                                    }>
                                        <span>
                                            <IconButton
                                                edge="end"
                                                color="error"
                                                size="small"
                                                disabled={!category.can_delete || busyId === category.id}
                                                onClick={() => requestDelete(category)}
                                                aria-label={`delete-${category.name}`}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            }
                        >
                            <ListItemText
                                primary={category.name}
                                secondary={
                                    category.expense_count > 0
                                        ? `${toPersianDigits(category.expense_count)} هزینه`
                                        : (category.is_active ? 'فعال' : 'غیرفعال')
                                }
                                sx={{ opacity: category.is_active ? 1 : 0.6 }}
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button onClick={onClose}>بستن</Button>
            </Box>

            <Dialog
                open={Boolean(confirmTarget)}
                onClose={() => setConfirmTarget(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>حذف دسته‌بندی</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        آیا از حذف دسته‌بندی «{confirmTarget?.name}» مطمئن هستید؟ این عمل قابل بازگشت نیست.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmTarget(null)}>انصراف</Button>
                    <Button color="error" variant="contained" onClick={performDelete}>
                        حذف
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
